import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { GridColDef } from '@mui/x-data-grid';
import React from 'react';
import { adminUserRoles } from './utils/enum';
import { IActionMenu, IGetPrjectMemberGridColumns } from './utils/interfaces';
import { IPrjectMemberGridColumns } from '../project-detail-view/type';

export const employeeDataGridColumns: GridColDef[] = [
  {
    field: 'id',
    headerName: '',
    width: 10,
    hide: true
  },
  {
    field: 'avatar',
    headerName: '',
    width: 50,
    renderCell: (params) => {
      return <>{<Avatar src={params.value} />}</>;
    }
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 250,
    renderCell: (params) => {
      return (
        <Typography sx={{ fontSize: '14px', fontWeight: '600px' }}>
          {params.value}
        </Typography>
      );
    }
  },
  {
    field: 'designation',
    headerName: 'Designation',
    width: 250,
    renderCell: (params) => {
      return (
        <Typography sx={{ fontSize: '14px', fontWeight: '600px' }}>
          {params.value}
        </Typography>
      );
    }
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 250
  },
  {
    field: 'contactNumber',
    headerName: 'Contact Number',
    width: 160
  }
];

export function getAllMemberGridColumns({
  anchorEl,
  setAnchorEl,
  rowId,
  setRowId,
  navigate,
  userRole
}: IActionMenu): GridColDef[] {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.up('md'));
  const allMemberDataGridColumn: GridColDef[] = [
    {
      field: 'id',
      headerName: '',
      [isSM ? 'flex' : 'width']: 10,
      hide: true
    },
    {
      field: 'profilePic',
      headerName: '',
      [isSM ? 'flex' : 'width']: 60,
      renderCell: (params) => {
        return (
          <>
            {
              <Avatar src={params.value}>
                {params.row.displayName
                  .split(' ')
                  .slice(0, 2)
                  .map((n: string) => n[0])
                  .join('')}
              </Avatar>
            }
          </>
        );
      }
    },
    {
      // ToDo-data of this column should be get using backend request instead of using urlparameters
      field: 'displayName',
      headerName: 'Name',
      filterable: false,
      hideable: false,

      [isSM ? 'flex' : 'width']: 250,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '600px' }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'role',
      headerName: 'Role',
      disableColumnMenu: true,
      [isSM ? 'flex' : 'width']: 150,
      renderCell: (params) => {
        return (
          <Typography>
            <Chip
              sx={{ backgroundColor: 'dataGridHeaderBackground.main' }}
              label={params.value}
            />
          </Typography>
        );
      }
    },
    {
      field: 'jobTitle',
      headerName: 'Designation ',
      filterable: false,
      [isSM ? 'flex' : 'width']: 300,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '600px' }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'actionId',
      headerName: 'Action',
      disableColumnMenu: true,
      [isSM ? 'flex' : 'width']: 150,
      renderCell: ({ row, id, value }) => {
        const handleClick = (
          event: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ): void => {
          setAnchorEl(
            anchorEl.map((_, ind) => {
              return Number(value) === Number(ind) ? event.currentTarget : _;
            })
          );
          setRowId(
            rowId.map((_, ind) =>
              Number(value) === Number(ind) ? Number(value) : _
            )
          );
        };

        const handleClose = (): void => {
          setAnchorEl(
            anchorEl.map((_, ind) => (Number(value) === Number(ind) ? null : _))
          );
          setRowId(
            rowId.map((_, ind) => (Number(value) === Number(ind) ? null : _))
          );
        };

        const handleLeaveHistory = (): void => {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          navigate(`/all-employee/leaves/?id=${id}&name=${row.displayName}`);
          handleClose();
        };
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '600px' }}>
            <IconButton onClick={handleClick}>
              <HistoryOutlinedIcon color="secondary" />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl[Number(value)]}
              open={Boolean(anchorEl[Number(value)])}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button'
              }}
            >
              <MenuItem onClick={handleLeaveHistory}>Leave History</MenuItem>
            </Menu>
          </Typography>
        );
      }
    },
    {
      field: 'mail',
      headerName: 'Email',
      [isSM ? 'flex' : 'width']: 250,
      filterable: false
    },
    {
      field: 'mobilePhone',
      headerName: 'Phone Number',
      [isSM ? 'flex' : 'width']: 200,
      disableColumnMenu: true
    }
  ];
  if (adminUserRoles.includes(userRole)) {
    return allMemberDataGridColumn.filter((item) =>
      allMemberDataGridColumnAdminViewFields.includes(item.field)
    );
  } else {
    return allMemberDataGridColumn.filter((item) =>
      allMemberDataGridColumnEmployeeViewFields.includes(item.field)
    );
  }
}

export function getProjectMemberGridColumns({
  canEdit = false,
  canDelete = false,
  isAdmin
}: IGetPrjectMemberGridColumns): IPrjectMemberGridColumns[] {
  const projectMemberDataGridColumn: IPrjectMemberGridColumns[] = [
    {
      field: 'id',
      headerName: 'No',
      minWidth: '5%'
    },
    {
      field: 'displayName',
      headerName: 'Name',
      minWidth: '20%'
    },
    {
      field: 'memberRole',
      headerName: 'Role',
      minWidth: '15%'
    },
    {
      field: 'hoursPerWeek',
      headerName: 'Hours Per Week',
      width: '15%'
    },
    {
      field: 'assignedAt',
      headerName: 'Added On',
      minWidth: '15%'
    },
    {
      field: 'memberStatus',
      headerName: 'Status',
      width: '15%'
    },
    {
      field: 'actionId',
      headerName: 'Actions',
      minWidth: '5%'
    }
  ];
  if (canEdit || canDelete) {
    return projectMemberDataGridColumn.filter((item) =>
      projectMemberDataGridColumnPMViewFields.includes(item.field)
    );
  } else if (isAdmin) {
    return projectMemberDataGridColumn.filter((item) =>
      projectMemberDataGridColumnAdminViewFields.includes(item.field)
    );
  }
  return projectMemberDataGridColumn.filter((item) =>
    projectMemberDataGridColumnMemberViewFields.includes(item.field)
  );
}

export function getResourceAllocationMemberGridColumns(): GridColDef[] {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.up('md'));

  const cellTypoStyle = {
    fontFamily: 'Be Vietnam Pro',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '24px',
    letterSpacing: '0.5px',
    textAlign: 'left'
  };
  const statusLabelMap = {
    PENDING: 'Inactive',
    APPROVED: 'Active',
    REJECTED: 'Inactive'
  };

  const statusColorMap = {
    PENDING: '#F69C31',
    APPROVED: '#06BF3A',
    REJECTED: '#F69C31'
  };

  const resourceAllocationDataGridColumn: GridColDef[] = [
    {
      field: 'projectName',
      headerName: 'Project Name',
      [isSM ? 'flex' : 'width']: 220,
      renderCell: (params) => (
        <Typography sx={cellTypoStyle}>{params.value}</Typography>
      )
    },
    {
      field: 'projectStatus',
      headerName: 'Status',
      [isSM ? 'flex' : 'width']: 150,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                display: 'inline-block',
                backgroundColor:
                  statusColorMap[params.value as keyof typeof statusColorMap] ||
                  'grey',
                marginRight: 1,
                verticalAlign: 'middle'
              }}
            />
            <Typography sx={cellTypoStyle}>
              {statusLabelMap[params.value as keyof typeof statusLabelMap] ||
                params.value}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'hoursPerWeek',
      headerName: 'Allocation(hours per week)',
      [isSM ? 'flex' : 'width']: 195,
      renderCell: (params) => (
        <Typography sx={cellTypoStyle}>{params.value}</Typography>
      )
    },
    {
      field: 'tentativeStartDate',
      headerName: 'Resource Start Date',
      [isSM ? 'flex' : 'width']: 160,
      renderCell: (params) => {
        const date = new Date(params.value);
        const formattedDate = `${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${date
          .getDate()
          .toString()
          .padStart(2, '0')}/${date.getFullYear()}`;
        return <Typography sx={cellTypoStyle}>{formattedDate}</Typography>;
      }
    },
    {
      field: 'tentativeEndDate',
      headerName: 'Resource End Date',
      [isSM ? 'flex' : 'width']: 160,
      renderCell: (params) => {
        const date = new Date(params.value);
        const formattedDate = `${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${date
          .getDate()
          .toString()
          .padStart(2, '0')}/${date.getFullYear()}`;
        return <Typography sx={cellTypoStyle}>{formattedDate}</Typography>;
      }
    }
  ];
  return resourceAllocationDataGridColumn.filter((item) =>
    resourceAllocationMemberDataGridColumnMemberViewFields.includes(item.field)
  );
}

export function getAllNotificationsDataGridColumns(): GridColDef[] {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.up('md'));
  return [
    {
      field: 'id',
      headerName: '',
      [isSM ? 'flex' : 'width']: 10,
      hide: true
    },
    {
      field: 'avatar',
      headerName: '',
      [isSM ? 'flex' : 'width']: 60,
      renderCell: (params) => {
        return <>{<Avatar src={params.value} />}</>;
      }
    },

    {
      field: 'name',
      headerName: '',
      filterable: false,
      hideable: false,
      [isSM ? 'flex' : 'width']: 300,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '500px' }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'message',
      headerName: '',
      filterable: false,
      hideable: false,
      [isSM ? 'flex' : 'width']: 550,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: '13px', fontWeight: '400px' }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'requestedAt',
      headerName: '',
      filterable: false,
      hideable: false,
      [isSM ? 'flex' : 'width']: 250,
      renderCell: (params) => {
        return (
          <Typography
            sx={{ fontSize: '12px', fontWeight: '300px', color: '#2F3033' }}
          >
            {params.value}
          </Typography>
        );
      }
    }
  ];
}

export const allMemberDataGridColumnAdminViewFields: string[] = [
  'id',
  'profilePic',
  'displayName',
  'role',
  'jobTitle',
  'actionId'
];

export const allMemberDataGridColumnEmployeeViewFields: string[] = [
  'id',
  'profilePic',
  'displayName',
  'jobTitle',
  'mail',
  'mobilePhone'
];
export const projectMemberDataGridColumnPMViewFields: string[] = [
  'id',
  'profilePic',
  'displayName',
  'memberRole',
  'hoursPerWeek',
  'assignedAt',
  'memberStatus',
  'actionId'
];

export const projectMemberDataGridColumnMemberViewFields: string[] = [
  'id',
  'profilePic',
  'displayName',
  'memberRole',
  'assignedAt'
];

export const projectMemberDataGridColumnAdminViewFields: string[] = [
  'id',
  'profilePic',
  'displayName',
  'memberRole',
  'hoursPerWeek',
  'assignedAt',
  'memberStatus'
];

export const resourceAllocationMemberDataGridColumnMemberViewFields: string[] =
  [
    'projectName',
    'projectStatus',
    'hoursPerWeek',
    'tentativeStartDate',
    'tentativeEndDate'
  ];
